import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { ChartLine, Storefront, Tag } from '@phosphor-icons/react'
import dayjs from '../../../utils/dayjsConfig'
import FilterUbiquaMui from '../../../components/Filters/FilterUbiquaMui/FilterUbiquaMui'
import {
  ROUTE_EXECUTION_STATUS_OPTIONS,
  ACTIVITIES_DIR,
  ACTIVITIES_OPTIONS,
  BOOLEAN_OPTIONS
} from '../helpers/userReportTabs'
import { buildUnifiedVisitLookupKey } from '../helpers/buildUnifiedVisitDetails'
import { buildRouteKpiValues } from '../helpers/routeKpiValues'
import SharedVisitsTable from '../components/SharedVisitsTable'
import RouteExecutionDateRangeCard from '../components/RouteExecutionDateRangeCard'
import RouteExecutionMobileFilters from '../mobile/RouteExecutionMobileFilters'
import {
  FILTER_KEYS,
  buildAppliedChipLabel,
  buildAppliedDateRange,
  buildSelectValue,
  countSelectedGroups,
  createEmptyDateRange,
  createEmptyDraftFilterSelections,
  dateRangeToPickerValue,
  dateRangeToQuery,
  formatDateLabel,
  formatDateRangeSummary,
  getSelectValues,
  normalizeDateRange,
  pickerValueToDateRange
} from '../helpers/routeExecutionFilters'
import { exportRouteExecutionTableCsv } from '../helpers/exportRouteExecutionTableCsv'
import RouteExecutionKpis from '../components/RouteExecutionKpis'

const STATUS_CHIP_STYLES = {
  visited: { label: 'VISITADO', variant: 'success' },
  no_visited: { label: 'NO VISITADO', variant: 'danger' },
  pending: { label: 'PENDIENTE', variant: 'warning' },
  no_location: { label: 'SIN GEOCERCA', variant: 'neutral' }
}

const ROUTE_STATUS_CHIP_CLASS_NAME = 'route-visit-status-chip'

const RouteExecutionTab = ({
  data,
  currentPage,
  setCurrentPage,
  totalPages,
  handlePaginationClick,
  onSearchQuery,
  searchQuery,
  routeFilterData,
  routeFilterLoading = false,
  routeFilterHasMore = false,
  routeFilterSearchQuery = '',
  onRouteFilterSearch,
  onRouteFilterLoadMore,
  routeCartData,
  geofenceSegment: persistedGeofenceSegment,
  onGeofenceSegmentChange,
  startDate,
  endDate,
  plannedDateQuery,
  setPlannedDateQuery,
  executedDateQuery,
  setExecutedDateQuery,
  setFilterQuery,
  isRouteInfoFetching,
  isKpiUpdating = false,
  filterSelections,
  onFilterSelectionsChange,
  selectedPlannedDates: persistedPlannedDates,
  onSelectedPlannedDatesChange,
  selectedExecutedDates: persistedExecutedDates,
  onSelectedExecutedDatesChange,
  salesmanName,
  onFetchRowDetails,
  formatActivityName,
  routeVisitDetailsByKey,
  routeVisitSummaryByKey,
  isMobileLayout = false,
  initialExpandedRowKey = null,
  totalCount = 0,
  perPage = 25,
  onRowsPerPageChange
}) => {
  const [selectedKpi, setSelectedKpi] = useState(null)
  const [localGeofenceSegment, setLocalGeofenceSegment] = useState('general')
  const geofenceSegment = persistedGeofenceSegment ?? localGeofenceSegment
  const activeGeofenceSegment = selectedKpi === 'geofence-active' ? geofenceSegment : null
  const searchInput = useRef(null)
  const [query, setQuery] = useState(searchQuery || '')
  const [filters, setFilters] = useState([])
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [idToLabelMapping, setIdToLabelMapping] = useState(new Map())
  const [localFilterSelections, setLocalFilterSelections] = useState(() => new Map())
  const allSelectedFilterOptions = filterSelections ?? localFilterSelections
  const setAllSelectedFilterOptions = onFilterSelectionsChange ?? setLocalFilterSelections
  const [filterVersion, setFilterVersion] = useState(0)
  const [localPlannedDates, setLocalPlannedDates] = useState(null)
  const [localExecutedDates, setLocalExecutedDates] = useState(null)
  const selectedPlannedDates =
    persistedPlannedDates === undefined ? localPlannedDates : persistedPlannedDates
  const selectedExecutedDates =
    persistedExecutedDates === undefined ? localExecutedDates : persistedExecutedDates
  const setSelectedPlannedDates = onSelectedPlannedDatesChange ?? setLocalPlannedDates
  const setSelectedExecutedDates = onSelectedExecutedDatesChange ?? setLocalExecutedDates
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [draftFilterSelections, setDraftFilterSelections] = useState(() =>
    createEmptyDraftFilterSelections()
  )
  const [draftPlannedDateRange, setDraftPlannedDateRange] = useState(() => createEmptyDateRange())
  const [draftExecutedDateRange, setDraftExecutedDateRange] = useState(() => createEmptyDateRange())

  const periodStart = useMemo(
    () => (startDate ? dayjs(startDate).startOf('day') : null),
    [startDate]
  )
  const periodEnd = useMemo(
    () => (endDate ? dayjs(endDate).startOf('day') : dayjs().startOf('day')),
    [endDate]
  )
  const constrainDateRange = useCallback(
    (range) => {
      const normalized = normalizeDateRange(range)
      if (
        (normalized.end && periodStart && normalized.end.isBefore(periodStart, 'day')) ||
        (normalized.start && normalized.start.isAfter(periodEnd, 'day'))
      ) {
        return createEmptyDateRange()
      }
      return normalizeDateRange({
        start:
          normalized.start && periodStart && normalized.start.isBefore(periodStart, 'day')
            ? periodStart
            : normalized.start,
        end: normalized.end && normalized.end.isAfter(periodEnd, 'day') ? periodEnd : normalized.end
      })
    },
    [periodStart, periodEnd]
  )

  useEffect(() => {
    const syncRange = (selected, setSelected, setDateQuery, startKey, endKey) => {
      const previous = pickerValueToDateRange(selected)
      const bounded = constrainDateRange(previous)
      const nextQuery = dateRangeToQuery(bounded, startKey, endKey)
      if (nextQuery !== dateRangeToQuery(previous, startKey, endKey)) {
        setSelected(dateRangeToPickerValue(bounded))
        setDateQuery(nextQuery)
        setCurrentPage(1)
      }
    }
    syncRange(
      selectedPlannedDates,
      setSelectedPlannedDates,
      setPlannedDateQuery,
      'planned_start',
      'planned_end'
    )
    syncRange(
      selectedExecutedDates,
      setSelectedExecutedDates,
      setExecutedDateQuery,
      'executed_start',
      'executed_end'
    )
    setDraftPlannedDateRange((range) => constrainDateRange(range))
    setDraftExecutedDateRange((range) => constrainDateRange(range))
  }, [
    constrainDateRange,
    selectedPlannedDates,
    selectedExecutedDates,
    setSelectedPlannedDates,
    setSelectedExecutedDates,
    setPlannedDateQuery,
    setExecutedDateQuery,
    setCurrentPage
  ])

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.value = searchQuery || ''
    }
    setQuery(searchQuery || '')
  }, [searchQuery])

  useEffect(() => {
    prepareFilterData()
  }, [routeFilterData])

  const activityOptions = useMemo(
    () =>
      ACTIVITIES_OPTIONS.filter((option) =>
        (routeFilterData?.activities || []).includes(option.value)
      ),
    [routeFilterData?.activities]
  )
  const kpiValues = buildRouteKpiValues(routeCartData, routeFilterData?.activities || [])

  const rows = useMemo(
    () =>
      (data || []).map((row, index) => {
        const statusStyle = STATUS_CHIP_STYLES[row.status] || STATUS_CHIP_STYLES.pending
        const lookupKey = buildUnifiedVisitLookupKey(row)
        const routeVisitSummary = lookupKey ? routeVisitSummaryByKey?.[lookupKey] || {} : {}
        const routeVisitDetails = lookupKey ? routeVisitDetailsByKey?.[lookupKey] || [] : []
        const totalVisitDuration =
          routeVisitSummary?.visitDuration && routeVisitSummary.visitDuration !== '-'
            ? routeVisitSummary.visitDuration
            : null

        return {
          rowKey: row.id || `${row.client_uid}-${row.visit_date}-${row.execution_date || index}`,
          rawRow: row,
          clientName: row.client_name,
          clientLabel: [row.client_uid, row.client_name].filter(Boolean).join(' - ') || '-',
          plannedDateLabel: row.in_route ? formatDateLabel(row.visit_date) : '-',
          executedDateLabel: row.execution_date ? formatDateLabel(row.execution_date) : '-',
          visitDuration:
            totalVisitDuration || row.summary?.visit_duration || row.visit_duration || '-',
          inRouteLabel: row.in_route ? 'SI' : 'NO',
          effectivenessLabel: row.execution_date != null ? (row.effective ? 'SI' : 'NO') : '-',
          activities: (row.activities || []).map(
            (activityCode) => ACTIVITIES_DIR[activityCode] || activityCode
          ),
          statusLabel: statusStyle.label,
          statusVariant: statusStyle.variant,
          statusChipClassName: ROUTE_STATUS_CHIP_CLASS_NAME,
          hasDetail: Boolean(routeVisitDetails.length)
        }
      }),
    [data, routeVisitDetailsByKey, routeVisitSummaryByKey]
  )

  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      submitSearch(searchInput.current?.value || '')
    }
  }

  const submitSearch = (value) => {
    const normalizedValue = value.toLowerCase()
    setQuery(normalizedValue)
    setCurrentPage(1)
    onSearchQuery(normalizedValue)
  }

  const handleMobileSearchChange = (event) => {
    const value = event.target.value
    setQuery(value)
    if (searchInput.current) {
      searchInput.current.value = value
    }
  }

  const handleMobileSearch = (event) => {
    if (event.key === 'Enter') {
      submitSearch(query)
    }
  }

  const deleteSearch = () => {
    setSelectedKpi(null)
    setQuery('')
    setCurrentPage(1)
    if (searchInput.current) {
      searchInput.current.value = ''
    }
    onSearchQuery('')
  }

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedFilterOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])
      const selectedIds = labels
        .map((label) => labelToIdMapping[label])
        .filter((value) => value !== undefined)
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
  }

  const filterMappings = {
    [FILTER_KEYS.client]: 'client_uid',
    [FILTER_KEYS.status]: 'status',
    [FILTER_KEYS.inRoute]: 'in_route',
    [FILTER_KEYS.geofence]: 'in_geofence',
    [FILTER_KEYS.effectiveness]: 'effective',
    [FILTER_KEYS.activities]: 'activities'
  }

  const buildFiltersQuery = () => {
    let queryParam = ''
    allSelectedFilterOptions.forEach((value, key) => {
      if (value.length !== 0) {
        const paramKey = filterMappings[key]
        if (paramKey) {
          queryParam += `&${paramKey}=${value.join(',')}`
        }
      }
    })
    // El KPI valida la ubicación de la actividad, no solo el estado de la visita.
    if (activeGeofenceSegment) {
      queryParam += `&geofence_activity_segment=${encodeURIComponent(activeGeofenceSegment)}`
    }
    setCurrentPage(1)
    setFilterQuery(queryParam)
  }

  const prepareFilterData = () => {
    const localLabelToIdMapping = {}
    const localIdToLabelMapping = new Map()
    const clients = routeFilterData?.clients || []

    const clientOptions = clients.map((client) => {
      const displayText = `${client.uid} - ${client.name}`
      localLabelToIdMapping[displayText] = client.uid
      localIdToLabelMapping.set(client.uid, displayText)
      return displayText
    })

    ACTIVITIES_OPTIONS.forEach((option) => {
      localLabelToIdMapping[option.name] = option.value
      localIdToLabelMapping.set(option.value, option.name)
    })

    BOOLEAN_OPTIONS.forEach((option) => {
      localLabelToIdMapping[option.name] = option.value
      localIdToLabelMapping.set(option.value, option.name)
    })

    ROUTE_EXECUTION_STATUS_OPTIONS.forEach((option) => {
      localLabelToIdMapping[option.name] = option.value
      localIdToLabelMapping.set(option.value, option.name)
    })

    setFilters(clientOptions.length ? [{ key: FILTER_KEYS.client, value: clientOptions }] : [])
    setLabelToIdMapping(localLabelToIdMapping)
    setIdToLabelMapping(localIdToLabelMapping)
  }

  const resetReactFilters = useCallback((activeKpi = null) => {
    setAllSelectedFilterOptions(new Map())
    setFilterQuery('')
    setQuery('')
    if (searchInput.current) searchInput.current.value = ''
    onSearchQuery('')
    setPlannedDateQuery(null)
    setExecutedDateQuery(null)
    setSelectedPlannedDates(null)
    setSelectedExecutedDates(null)
    setDraftFilterSelections(createEmptyDraftFilterSelections())
    setDraftPlannedDateRange(createEmptyDateRange())
    setDraftExecutedDateRange(createEmptyDateRange())
    setIsMobileFiltersOpen(false)
    setSelectedKpi(activeKpi)
    setFilterVersion((prevVersion) => prevVersion + 1)
    setCurrentPage(1)
  }, [
    setAllSelectedFilterOptions,
    setFilterQuery,
    onSearchQuery,
    setPlannedDateQuery,
    setExecutedDateQuery,
    setSelectedPlannedDates,
    setSelectedExecutedDates,
    setCurrentPage
  ])

  const previousFilterSelections = useRef(allSelectedFilterOptions)
  const previousGeofenceSegment = useRef(activeGeofenceSegment)
  useEffect(() => {
    if (
      previousFilterSelections.current === allSelectedFilterOptions &&
      previousGeofenceSegment.current === activeGeofenceSegment
    ) {
      return
    }
    previousFilterSelections.current = allSelectedFilterOptions
    previousGeofenceSegment.current = activeGeofenceSegment
    buildFiltersQuery()
  }, [allSelectedFilterOptions, activeGeofenceSegment])

  const isAnyFilterSelected = () => {
    const filtersSelected = Array.from(allSelectedFilterOptions.entries()).some(
      ([, value]) => value.length > 0
    )
    const plannedDateSelected = plannedDateQuery !== null && plannedDateQuery.trim() !== ''
    const executedDateSelected = executedDateQuery !== null && executedDateQuery.trim() !== ''

    return filtersSelected || plannedDateSelected || executedDateSelected
  }

  const filtersArray = Array.isArray(filters) ? filters : Array.from(filters || [])
  const anyFilterEmpty = filtersArray.some((filter) => filter.value.length === 0)

  const ubiquaFilters = useMemo(
    () => [
      {
        key: FILTER_KEYS.client,
        label: 'Cliente',
        icon: Storefront,
        inline: true,
        searchPlaceholder: 'Buscar ...',
        options: (filtersArray.find((filter) => filter.key === FILTER_KEYS.client)?.value || [])
          .map((label) => ({
            label,
            value: labelToIdMapping[label]
          }))
          .filter((option) => option.value !== undefined),
        loading: routeFilterLoading,
        hasMore: routeFilterHasMore,
        remoteSearchQuery: routeFilterSearchQuery,
        onSearchChange: onRouteFilterSearch,
        onLoadMore: onRouteFilterLoadMore
      },
      {
        key: FILTER_KEYS.status,
        label: 'Estado',
        icon: Tag,
        inline: true,
        options: ROUTE_EXECUTION_STATUS_OPTIONS.map((option) => ({
          label: option.name,
          value: option.value
        }))
      },
      {
        key: FILTER_KEYS.inRoute,
        label: 'Dentro de ruta',
        inline: false,
        options: BOOLEAN_OPTIONS.map((option) => ({ label: option.name, value: option.value }))
      },
      {
        key: FILTER_KEYS.geofence,
        label: 'Dentro de geocerca',
        inline: false,
        options: BOOLEAN_OPTIONS.map((option) => ({ label: option.name, value: option.value }))
      },
      {
        key: FILTER_KEYS.effectiveness,
        label: 'Efectividad',
        icon: ChartLine,
        inline: true,
        options: BOOLEAN_OPTIONS.map((option) => ({
          label: option.name,
          value: option.value
        }))
      },
      {
        key: FILTER_KEYS.activities,
        label: 'Actividades',
        inline: false,
        options: activityOptions.map((option) => ({
          label: option.name,
          value: option.value
        }))
      }
    ],
    [
      filtersArray,
      labelToIdMapping,
      onRouteFilterLoadMore,
      onRouteFilterSearch,
      routeFilterHasMore,
      routeFilterLoading,
      routeFilterSearchQuery,
      activityOptions
    ]
  )

  const handleUbiquaFilterChange = (filterKey, nextValue) => {
    setSelectedKpi(null)
    setAllSelectedFilterOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      updatedOptions.set(filterKey, getSelectValues(nextValue))
      return updatedOptions
    })
  }

  // Las tarjetas son filtros excluyentes: cada una reemplaza completamente la
  // selección anterior, incluida la búsqueda, las fechas y los borradores.
  const handleKpiSelect = useCallback(
    (kpi) => {
      if (selectedKpi === kpi) {
        resetReactFilters()
        return
      }

      const kpiFilters = {
        effectiveness: {},
        planned: {
          [FILTER_KEYS.inRoute]: [true]
        },
        visited: {
          [FILTER_KEYS.status]: ['visited'],
          [FILTER_KEYS.inRoute]: [true]
        },
        unvisited: {
          [FILTER_KEYS.status]: ['no_visited', 'pending', 'no_location'],
          [FILTER_KEYS.inRoute]: [true]
        },
        outside: {
          [FILTER_KEYS.status]: ['visited'],
          [FILTER_KEYS.inRoute]: [false]
        },
        conversion: {},
        'geofence-visited': { [FILTER_KEYS.status]: ['visited'] },
        'geofence-active': {
          [FILTER_KEYS.status]: ['visited'],
          [FILTER_KEYS.geofence]: [true],
          [FILTER_KEYS.effectiveness]: [true],
          ...(geofenceSegment && geofenceSegment !== 'general'
            ? { [FILTER_KEYS.activities]: [geofenceSegment] }
            : {})
        }
      }
      const nextFilters = kpiFilters[kpi]
      if (nextFilters === undefined) return

      resetReactFilters(kpi)
      setAllSelectedFilterOptions(() => {
        const nextSelections = new Map()
        Object.entries(nextFilters).forEach(([filterKey, values]) => {
          nextSelections.set(filterKey, values)
        })
        return nextSelections
      })
    },
    [selectedKpi, geofenceSegment, setAllSelectedFilterOptions, resetReactFilters]
  )

  // La pestaña de geocerca normalmente solo cambia el KPI que se muestra. Pero
  // si el KPI de actividad está seleccionado, también representa un filtro: al
  // pasar de General a Pedido/Entrega/Cobro solo cambia la actividad solicitada al API.
  const handleGeofenceSegmentChange = useCallback(
    (nextSegment) => {
      setLocalGeofenceSegment(nextSegment)
      onGeofenceSegmentChange?.(nextSegment)

      if (selectedKpi !== 'geofence-active') return

      setAllSelectedFilterOptions((previousSelections) => {
        const nextSelections = new Map(previousSelections)

        if (nextSegment === 'general') {
          nextSelections.delete(FILTER_KEYS.activities)
        } else {
          nextSelections.set(FILTER_KEYS.activities, [nextSegment])
        }

        return nextSelections
      })
    },
    [onGeofenceSegmentChange, selectedKpi, setAllSelectedFilterOptions]
  )

  const handleUbiquaSearchChange = (event) => {
    const value = event.target.value
    setQuery(value)
    if (searchInput.current) {
      searchInput.current.value = value
    }

    // Al borrar el texto manualmente (incluido Ctrl/Cmd+A + Supr), no hay Enter
    // ni clic en la X. Sólo restablecemos si había una búsqueda aplicada para
    // evitar volver a consultar mientras el usuario simplemente está editando.
    if (!value && searchQuery) {
      submitSearch('')
    }
  }

  const handleUbiquaSearchEnter = (event) => {
    if (event.key === 'Enter') {
      submitSearch(query)
    }
  }

  const mobileFilterConfigs = useMemo(
    () => [
      {
        key: FILTER_KEYS.client,
        options: (filters.find((filter) => filter.key === FILTER_KEYS.client)?.value || [])
          .map((label) => ({
            label,
            value: labelToIdMapping[label]
          }))
          .filter((option) => option.value !== undefined),
        loading: routeFilterLoading,
        hasMore: routeFilterHasMore,
        onSearchChange: onRouteFilterSearch,
        onLoadMore: onRouteFilterLoadMore
      },
      {
        key: FILTER_KEYS.status,
        options: ROUTE_EXECUTION_STATUS_OPTIONS.map((option) => ({
          label: option.name,
          value: option.value
        }))
      },
      {
        key: FILTER_KEYS.inRoute,
        options: BOOLEAN_OPTIONS.map((option) => ({ label: option.name, value: option.value }))
      },
      {
        key: FILTER_KEYS.geofence,
        options: BOOLEAN_OPTIONS.map((option) => ({ label: option.name, value: option.value }))
      },
      {
        key: FILTER_KEYS.effectiveness,
        options: BOOLEAN_OPTIONS.map((option) => ({
          label: option.name,
          value: option.value
        }))
      },
      {
        key: FILTER_KEYS.activities,
        options: activityOptions.map((option) => ({
          label: option.name,
          value: option.value
        }))
      }
    ],
    [
      filters,
      labelToIdMapping,
      onRouteFilterLoadMore,
      onRouteFilterSearch,
      routeFilterHasMore,
      routeFilterLoading,
      activityOptions
    ]
  )

  const appliedFilterSelections = useMemo(
    () => ({
      [FILTER_KEYS.client]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.client)),
      [FILTER_KEYS.status]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.status)),
      [FILTER_KEYS.inRoute]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.inRoute)),
      [FILTER_KEYS.geofence]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.geofence)),
      [FILTER_KEYS.effectiveness]: buildSelectValue(
        allSelectedFilterOptions.get(FILTER_KEYS.effectiveness)
      ),
      [FILTER_KEYS.activities]: buildSelectValue(
        allSelectedFilterOptions.get(FILTER_KEYS.activities)
      )
    }),
    [allSelectedFilterOptions]
  )

  const appliedPlannedDateRange = useMemo(
    () => buildAppliedDateRange(pickerValueToDateRange(selectedPlannedDates)),
    [selectedPlannedDates]
  )
  const appliedExecutedDateRange = useMemo(
    () => buildAppliedDateRange(pickerValueToDateRange(selectedExecutedDates)),
    [selectedExecutedDates]
  )

  const supplementaryAppliedGroups = useMemo(() => {
    const groups = []
    if (appliedPlannedDateRange.start) {
      groups.push({
        key: 'planned-range',
        label: 'Planificada',
        values: [
          {
            value: 'planned-range',
            label: formatDateRangeSummary(appliedPlannedDateRange, '')
          }
        ],
        onRemove: () => {
          setSelectedKpi(null)
          setPlannedDateQuery(null)
          setSelectedPlannedDates(null)
          setCurrentPage(1)
        }
      })
    }

    if (appliedExecutedDateRange.start) {
      groups.push({
        key: 'executed-range',
        label: 'Ejecucion',
        values: [
          {
            value: 'executed-range',
            label: formatDateRangeSummary(appliedExecutedDateRange, '')
          }
        ],
        onRemove: () => {
          setSelectedKpi(null)
          setExecutedDateQuery(null)
          setSelectedExecutedDates(null)
          setCurrentPage(1)
        }
      })
    }

    return groups
  }, [
    appliedExecutedDateRange,
    appliedPlannedDateRange,
    setCurrentPage
  ])

  const supplementaryActiveCount = useMemo(() => {
    let count = 0
    if (appliedPlannedDateRange.start) count += 1
    if (appliedExecutedDateRange.start) count += 1
    return count
  }, [appliedExecutedDateRange, appliedPlannedDateRange])

  const supplementaryDraftCount = useMemo(() => {
    let count = 0
    if (buildAppliedDateRange(constrainDateRange(draftPlannedDateRange)).start) count += 1
    if (buildAppliedDateRange(constrainDateRange(draftExecutedDateRange)).start) count += 1
    return count
  }, [draftExecutedDateRange, draftPlannedDateRange, constrainDateRange])

  const applyDesktopFilters = (selections) => {
    setSelectedKpi(null)
    const nextSelectedFilterOptions = new Map()
    Object.values(FILTER_KEYS).forEach((filterKey) => {
      nextSelectedFilterOptions.set(filterKey, [...getSelectValues(selections[filterKey])])
    })
    setAllSelectedFilterOptions(nextSelectedFilterOptions)

    const nextPlannedDateRange = buildAppliedDateRange(constrainDateRange(draftPlannedDateRange))
    const nextExecutedDateRange = buildAppliedDateRange(constrainDateRange(draftExecutedDateRange))
    setSelectedPlannedDates(dateRangeToPickerValue(nextPlannedDateRange))
    setSelectedExecutedDates(dateRangeToPickerValue(nextExecutedDateRange))
    setPlannedDateQuery(dateRangeToQuery(nextPlannedDateRange, 'planned_start', 'planned_end'))
    setExecutedDateQuery(dateRangeToQuery(nextExecutedDateRange, 'executed_start', 'executed_end'))
    setCurrentPage(1)
  }

  const resetDesktopDrawerDraft = () => {
    setDraftPlannedDateRange(createEmptyDateRange())
    setDraftExecutedDateRange(createEmptyDateRange())
  }

  const appliedFilterGroupCount = useMemo(
    () =>
      countSelectedGroups(
        appliedFilterSelections,
        appliedPlannedDateRange,
        appliedExecutedDateRange
      ),
    [
      appliedExecutedDateRange,
      appliedFilterSelections,
      appliedPlannedDateRange
    ]
  )

  const appliedFilterChips = useMemo(() => {
    const chips = []
    const clientValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.client])
    const statusValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.status])
    const inRouteValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.inRoute])
    const geofenceValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.geofence])
    const effectivenessValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.effectiveness])
    const activityValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.activities])

    if (clientValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.client,
        label: buildAppliedChipLabel({
          singularLabel: 'Cliente',
          pluralLabel: 'Clientes',
          values: clientValues,
          idToLabelMapping
        })
      })
    }

    if (statusValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.status,
        label: buildAppliedChipLabel({
          singularLabel: 'Estado',
          pluralLabel: 'Estados',
          values: statusValues,
          idToLabelMapping
        })
      })
    }

    if (inRouteValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.inRoute,
        label: buildAppliedChipLabel({
          singularLabel: 'Dentro de ruta',
          pluralLabel: 'Dentro de ruta',
          values: inRouteValues,
          idToLabelMapping
        })
      })
    }

    if (geofenceValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.geofence,
        label: buildAppliedChipLabel({
          singularLabel: 'Dentro de geocerca',
          pluralLabel: 'Dentro de geocerca',
          values: geofenceValues,
          idToLabelMapping
        })
      })
    }

    if (effectivenessValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.effectiveness,
        label: buildAppliedChipLabel({
          singularLabel: 'Efectividad',
          pluralLabel: 'Efectividad',
          values: effectivenessValues,
          idToLabelMapping
        })
      })
    }

    if (activityValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.activities,
        label: buildAppliedChipLabel({
          singularLabel: 'Actividad',
          pluralLabel: 'Actividades',
          values: activityValues,
          idToLabelMapping
        })
      })
    }

    if (appliedPlannedDateRange.start) {
      chips.push({
        key: 'planned-range',
        label: `Planificada: ${formatDateRangeSummary(appliedPlannedDateRange, '')}`
      })
    }

    if (appliedExecutedDateRange.start) {
      chips.push({
        key: 'executed-range',
        label: `Ejecucion: ${formatDateRangeSummary(appliedExecutedDateRange, '')}`
      })
    }

    return chips
  }, [
    appliedExecutedDateRange,
    appliedFilterSelections,
    appliedPlannedDateRange,
    idToLabelMapping
  ])

  const buildDraftSelectionsFromApplied = () => ({
    [FILTER_KEYS.client]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.client)),
    [FILTER_KEYS.status]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.status)),
    [FILTER_KEYS.inRoute]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.inRoute)),
    [FILTER_KEYS.geofence]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.geofence)),
    [FILTER_KEYS.effectiveness]: buildSelectValue(
      allSelectedFilterOptions.get(FILTER_KEYS.effectiveness)
    ),
    [FILTER_KEYS.activities]: buildSelectValue(allSelectedFilterOptions.get(FILTER_KEYS.activities))
  })

  const syncMobileDraftWithApplied = () => {
    setDraftFilterSelections(buildDraftSelectionsFromApplied())
    setDraftPlannedDateRange(pickerValueToDateRange(selectedPlannedDates))
    setDraftExecutedDateRange(pickerValueToDateRange(selectedExecutedDates))
  }

  const handleOpenMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(true)
  }

  const handleCloseMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(false)
  }

  const handleDraftSelectChange = (filterKey, nextValue) => {
    setDraftFilterSelections((prevValue) => ({
      ...prevValue,
      [filterKey]: buildSelectValue(getSelectValues(nextValue))
    }))
  }

  const clearDraftFilters = () => {
    setDraftFilterSelections(createEmptyDraftFilterSelections())
    setDraftPlannedDateRange(createEmptyDateRange())
    setDraftExecutedDateRange(createEmptyDateRange())
  }

  const clearAllMobileFilters = () => {
    clearDraftFilters()
    resetReactFilters()
    setIsMobileFiltersOpen(false)
  }

  const applyMobileFilters = () => {
    setSelectedKpi(null)
    const nextSelectedFilterOptions = new Map()
    const nextPlannedDateRange = buildAppliedDateRange(constrainDateRange(draftPlannedDateRange))
    const nextExecutedDateRange = buildAppliedDateRange(constrainDateRange(draftExecutedDateRange))

    Object.values(FILTER_KEYS).forEach((filterKey) => {
      nextSelectedFilterOptions.set(filterKey, [
        ...getSelectValues(draftFilterSelections[filterKey])
      ])
    })

    setAllSelectedFilterOptions(nextSelectedFilterOptions)
    setSelectedPlannedDates(dateRangeToPickerValue(nextPlannedDateRange))
    setSelectedExecutedDates(dateRangeToPickerValue(nextExecutedDateRange))
    setPlannedDateQuery(dateRangeToQuery(nextPlannedDateRange, 'planned_start', 'planned_end'))
    setExecutedDateQuery(dateRangeToQuery(nextExecutedDateRange, 'executed_start', 'executed_end'))
    setIsMobileFiltersOpen(false)
  }

  const renderDateRangeCard = ({ rangeKey, title, range }) => (
    <RouteExecutionDateRangeCard
      title={title}
      range={range}
      minDate={periodStart}
      maxDate={periodEnd}
      onChange={(nextRange) => {
        const setRange =
          rangeKey === 'planned' ? setDraftPlannedDateRange : setDraftExecutedDateRange
        setRange(constrainDateRange(nextRange))
      }}
    />
  )

  const mobileFiltersBlock = (
    <RouteExecutionMobileFilters
      query={query}
      handleSearchChange={handleMobileSearchChange}
      handleSearchKeyDown={handleMobileSearch}
      searchInputRef={searchInput}
      clearSearch={deleteSearch}
      openFilters={handleOpenMobileFilters}
      appliedFilterGroupCount={appliedFilterGroupCount}
      appliedFilterChips={appliedFilterChips}
      isOpen={isMobileFiltersOpen}
      closeFilters={handleCloseMobileFilters}
      mobileFilterConfigs={mobileFilterConfigs}
      draftFilterSelections={draftFilterSelections}
      buildSelectValue={buildSelectValue}
      handleDraftSelectChange={handleDraftSelectChange}
      anyFilterEmpty={anyFilterEmpty}
      plannedDateRangeCard={renderDateRangeCard({
        rangeKey: 'planned',
        title: 'Fecha planificada',
        range: draftPlannedDateRange
      })}
      executedDateRangeCard={renderDateRangeCard({
        rangeKey: 'executed',
        title: 'Fecha de ejecución',
        range: draftExecutedDateRange
      })}
      clearAllFilters={clearAllMobileFilters}
      applyFilters={applyMobileFilters}
    />
  )

  return (
    <div className="rp-preview rp-connected-route route-execution-tab">
      <RouteExecutionKpis
        isUpdating={isKpiUpdating}
        selected={selectedKpi}
        onSelect={handleKpiSelect}
        values={kpiValues}
        segment={geofenceSegment}
        onSegmentChange={handleGeofenceSegmentChange}
      />
      <div>
        <div className="show-area-reports show-area-reports--tower-shell mt-30">
          {isMobileLayout ? (
            mobileFiltersBlock
          ) : (
            <FilterUbiquaMui
              key={filterVersion}
              title="Filtrar visitas"
              searchPlaceholder="Buscar por código o nombre de cliente..."
              compactSearch
              filters={ubiquaFilters}
              appliedFilterSelections={appliedFilterSelections}
              isAnyFilterSelected={isAnyFilterSelected()}
              searchInput={query}
              disabled={anyFilterEmpty}
              defaultInlineFilterCount={3}
              drawerPreviewEmptyLabel="Todas las visitas"
              supplementaryAppliedGroups={supplementaryAppliedGroups}
              supplementaryFilterCount={2}
              supplementaryActiveCount={supplementaryActiveCount}
              supplementaryDraftCount={supplementaryDraftCount}
              onFilterChange={handleUbiquaFilterChange}
              onApplyFilters={applyDesktopFilters}
              onResetFilters={resetReactFilters}
              onDrawerOpen={syncMobileDraftWithApplied}
              onDrawerReset={resetDesktopDrawerDraft}
              onSearchChange={handleUbiquaSearchChange}
              onSearchEnter={handleUbiquaSearchEnter}
              onSearchClear={deleteSearch}
              onExport={() => exportRouteExecutionTableCsv(rows, routeVisitDetailsByKey)}
              exportDisabled={isRouteInfoFetching || rows.length === 0}
              exportTooltip="Descargar página actual con detalles en CSV"
              drawerExtraContent={
                <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0, mt: 2.5 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid #dce6ef',
                      borderRadius: '18px',
                      bgcolor: '#f4f8fc'
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#597fa9', mb: 0.5 }}>
                      Período permitido
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#273e54' }}>
                      {periodStart
                        ? `${periodStart.format('DD/MM/YYYY')} – ${periodEnd.format('DD/MM/YYYY')}`
                        : `Hasta el ${periodEnd.format('DD/MM/YYYY')}`}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#7890a5', mt: 0.5 }}>
                      Selecciona tus fechas dentro de este rango.
                    </Typography>
                  </Box>
                  {renderDateRangeCard({
                    rangeKey: 'planned',
                    title: 'Fecha planificada',
                    range: draftPlannedDateRange
                  })}
                  {renderDateRangeCard({
                    rangeKey: 'executed',
                    title: 'Fecha de ejecución',
                    range: draftExecutedDateRange
                  })}
                </Stack>
              }
            />
          )}
        </div>
      </div>

      <div className="route-tower-table-shell ubiqua-data-table-shell">
        <SharedVisitsTable
          variant="route"
          rows={rows}
          isLoading={isRouteInfoFetching}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={perPage}
          onRowsPerPageChange={(_, nextPerPage) => onRowsPerPageChange?.(nextPerPage)}
          onPaginate={handlePaginationClick}
          onFetchRowDetails={onFetchRowDetails}
          salesmanName={salesmanName}
          formatActivityName={formatActivityName}
          isMobileLayout={isMobileLayout}
          hidePagination={isMobileLayout}
          initialExpandedRowKey={initialExpandedRowKey}
          onPageChange={setCurrentPage}
          towerLayout={!isMobileLayout}
          paginationItemLabel="visitas"
          showPageSizeControl
        />
      </div>
    </div>
  )
}

export default function RouteExecutionTabView(props) {
  return <RouteExecutionTab {...props} />
}
